import { useState, useEffect } from "react";
import { Modal } from "@mui/material";
import { Button, Card, CardBody, Typography } from "@material-tailwind/react";
import { XMarkIcon, EyeIcon, DocumentArrowDownIcon, ShieldCheckIcon } from "@heroicons/react/24/solid";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";

/* Services */
import {
  downloadSafetyDiagReport,
  previewSafetyDiagReport,
  SafetyDiagReportRequest,
} from "../../../../services/export/safetyDiagReport";
import { api } from "../../../../services/api/api";

/* Styles */
import "../../../../components/export-button/ExportModal.scss";

export interface SafetyDiagExportModalProps {
  open: boolean;
  onClose: () => void;
  workOrderId: string;
  workOrderRef?: string;
  realizationId?: string;
  attemptLabel?: string;
  languageIsoCode?: string;
  customerLanguages?: any[];
  customer?: EasyTech.Customer;
}

interface LanguageItem {
  id: string;
  isoCode: string;
  label: string;
  isDefault?: boolean;
}

const LANGUAGE_NAMES: Record<string, string> = {
  fr: "Français",
  en: "English",
  de: "Deutsch",
  nl: "Nederlands",
  pl: "Polski",
  ro: "Română",
  it: "Italiano",
  es: "Español",
  ca: "Català",
};

export default function SafetyDiagExportModal({
  open,
  onClose,
  workOrderId,
  workOrderRef,
  realizationId,
  attemptLabel,
  languageIsoCode,
  customerLanguages: initialCustomerLanguages,
  customer,
}: SafetyDiagExportModalProps) {
  const { t, i18n } = useTranslation(["diagSecu", "workorder"]);
  const [customerLanguages, setCustomerLanguages] = useState<any[]>(initialCustomerLanguages || []);
  const [isLoadingLanguages, setIsLoadingLanguages] = useState<boolean>(false);
  const [busyAction, setBusyAction] = useState<string | null>(null);

  useEffect(() => {
    if (initialCustomerLanguages && initialCustomerLanguages.length > 0) {
      setCustomerLanguages(initialCustomerLanguages);
      return;
    }

    if (open && customer?.id && (!customerLanguages || customerLanguages.length === 0)) {
      let active = true;
      setIsLoadingLanguages(true);
      api
        .get(`users/customers/${customer.id}`)
        .then((data: any) => {
          if (active && Array.isArray(data?.languages)) {
            setCustomerLanguages(data.languages);
          }
        })
        .catch((err) => {
          console.error("Erreur lors de la récupération des langues du client:", err);
        })
        .finally(() => {
          if (active) setIsLoadingLanguages(false);
        });

      return () => {
        active = false;
      };
    }
  }, [open, customer?.id, initialCustomerLanguages]);

  // Préparation de la liste des langues à afficher
  const languagesToDisplay: LanguageItem[] = (() => {
    if (customerLanguages && customerLanguages.length > 0) {
      return customerLanguages.map((cl: any, idx: number) => {
        const iso = (cl.language?.isoCode || cl.isoCode || "").toLowerCase();
        const label = cl.language?.label || cl.label || LANGUAGE_NAMES[iso] || iso.toUpperCase();
        return {
          id: cl.id || cl.language?.id || `lang-${iso}-${idx}`,
          isoCode: iso,
          label,
          isDefault: !!cl.default,
        };
      });
    }

    // Fallback sur la langue du rapport ou de l'application si aucune langue n'est configurée chez le client
    const fallbackIso = (languageIsoCode || i18n.language || "en").split("-")[0].toLowerCase();
    const fallbackLabel = LANGUAGE_NAMES[fallbackIso] || fallbackIso.toUpperCase();
    return [
      {
        id: `fallback-${fallbackIso}`,
        isoCode: fallbackIso,
        label: fallbackLabel,
        isDefault: true,
      },
    ];
  })();

  const createRequest = (langIsoCode?: string): SafetyDiagReportRequest => ({
    workOrderId,
    workOrderRef,
    realizationId,
    attemptLabel,
    languageIsoCode: langIsoCode,
  });

  const handlePreview = async (langIsoCode?: string) => {
    if (busyAction) return;
    setBusyAction(`preview-${langIsoCode}`);
    try {
      await previewSafetyDiagReport(createRequest(langIsoCode));
      toast.success(t("diagSecu:report:export:previewSuccess", { defaultValue: "Aperçu ouvert dans un nouvel onglet" }));
    } catch (error) {
      console.error("Erreur lors de l'aperçu du PDF:", error);
      toast.error(t("diagSecu:report:export:error", { defaultValue: "Impossible de générer le PDF." }));
    } finally {
      setBusyAction(null);
    }
  };

  const handleDownload = async (langIsoCode?: string) => {
    if (busyAction) return;
    setBusyAction(`download-${langIsoCode}`);
    try {
      await downloadSafetyDiagReport(createRequest(langIsoCode));
      toast.success(t("diagSecu:report:export:downloadSuccess", { defaultValue: "Téléchargement réussi" }));
    } catch (error) {
      console.error("Erreur lors du téléchargement du PDF:", error);
      toast.error(t("diagSecu:report:export:error", { defaultValue: "Impossible de générer le PDF." }));
    } finally {
      setBusyAction(null);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      className="te-modal"
      aria-labelledby="safety-diag-export-modal"
    >
      <Card className="te-modal-content rounded-lg">
        <button
          className="close-button"
          onClick={onClose}
          disabled={busyAction !== null}
          aria-label="Fermer"
        >
          <XMarkIcon className="w-4 h-4" />
        </button>

        <CardBody className="p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="rounded-full border p-2.5 bg-blue-100 border-blue-200">
              <ShieldCheckIcon className="w-7 h-7 text-blue-700" />
            </div>
            <Typography variant="h5" className="text-gray-900 font-semibold">
              {t("diagSecu:report:export:modalTitle", {
                defaultValue: "Download the safety diagnosis report",
              })}
            </Typography>
          </div>

          <div className="flex flex-col gap-3">
            <Typography variant="h6" className="text-sm font-medium text-gray-700">
              {t("workorder:report:languageOptions", { defaultValue: "Customer languages" })}
            </Typography>

            {isLoadingLanguages ? (
              <div className="flex items-center justify-center p-6">
                <Typography className="text-sm text-gray-500">
                  {t("workorder:report:loadingLanguages", { defaultValue: "Chargement des langues…" })}
                </Typography>
              </div>
            ) : (
              <div className="max-h-72 overflow-y-auto">
                <div className="grid grid-cols-1 gap-2.5">
                  {languagesToDisplay.map((lang) => (
                    <div
                      key={lang.id}
                      className="flex items-center justify-between p-3.5 border rounded-lg bg-gray-50 border-gray-200"
                    >
                      <div className="flex flex-col">
                        <Typography variant="h6" className="text-sm font-medium text-gray-900">
                          {lang.label}
                        </Typography>
                        <Typography className="text-xs text-gray-500">
                          {t("workorder:report:languageDescription", {
                            language: lang.label,
                            defaultValue: `Generate report in ${lang.label}`,
                          })}
                        </Typography>
                      </div>

                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outlined"
                          color="blue"
                          className="flex items-center gap-1.5 normal-case font-medium border-blue-500 text-blue-500"
                          disabled={busyAction !== null}
                          onClick={() => handlePreview(lang.isoCode)}
                        >
                          <EyeIcon className="w-4 h-4" />
                          {t("diagSecu:report:export:preview", { defaultValue: "PREVIEW" })}
                        </Button>

                        <Button
                          size="sm"
                          color="blue"
                          className="flex items-center gap-1.5 bg-blue-500 normal-case font-medium text-white shadow-md hover:bg-blue-600"
                          disabled={busyAction !== null}
                          onClick={() => handleDownload(lang.isoCode)}
                        >
                          <DocumentArrowDownIcon className="w-4 h-4" />
                          {busyAction === `download-${lang.isoCode}`
                            ? t("diagSecu:report:export:downloading", { defaultValue: "Préparation…" })
                            : t("diagSecu:report:export:download", { defaultValue: "DOWNLOAD" })}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </CardBody>
      </Card>
    </Modal>
  );
}
